import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Navigate, Route, Routes, useNavigate, useParams, useSearchParams } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import Iconify from "../components/iconify/Iconify";
import Loading from "../components/spinners/Loading";
import {
  useCreateNewsMutation,
  useDeleteNewsMutation,
  useGetNewsByIdQuery,
  useGetNewsQuery,
  useGetSystemNewsQuery,
  useUpdateNewsUserStateMutation,
  useUpdateNewsMutation,
} from "../features/news/newsApiSlice";
import { useGetSitesQuery } from "../features/sites/sitesApiSlice";
import { sanitizeRichTextHtml, getPlainTextFromHtml } from "../utils/sanitizeRichTextHtml";
import SimpleRichTextEditor from "../components/editor/SimpleRichTextEditor";

const TARGETING_OPTIONS = [
  {
    value: "ALL_MEMBERS",
    label: "Alle Mitglieder",
    helper: "Sichtbar für alle Mitglieder des Vereins.",
  },
  {
    value: "SITE_MEMBERS",
    label: "Standort-spezifisch",
    helper: "Nur Mitglieder eines bestimmten Standorts sehen diese Neuigkeit.",
  },
  {
    value: "TRAINERS",
    label: "Nur Trainer",
    helper: "Nur Trainer sehen diese Neuigkeit.",
  },
  {
    value: "EDITORS",
    label: "Nur Editoren",
    helper: "Nur Editoren sehen diese Neuigkeit.",
  },
  {
    value: "TRAINERS_EDITORS",
    label: "Trainer und Editoren",
    helper: "Sichtbar für Trainer und Editoren.",
  },
];

const mapTargetingToPayload = (targeting) => {
  switch (targeting) {
    case "SITE_MEMBERS":
      return { scope: "SITE", targetGroups: ["ALL_MEMBERS"] };
    case "TRAINERS":
      return { scope: "GLOBAL", targetGroups: ["TRAINERS"] };
    case "EDITORS":
      return { scope: "GLOBAL", targetGroups: ["EDITORS"] };
    case "TRAINERS_EDITORS":
      return { scope: "GLOBAL", targetGroups: ["TRAINERS", "EDITORS"] };
    case "ALL_MEMBERS":
    default:
      return { scope: "GLOBAL", targetGroups: ["ALL_MEMBERS"] };
  }
};

const mapPayloadToTargeting = (item) => {
  const targetGroups = Array.isArray(item?.targetGroups) ? item.targetGroups : [];
  if (item?.scope === "SITE") return "SITE_MEMBERS";
  if (targetGroups.includes("TRAINERS") && targetGroups.includes("EDITORS")) {
    return "TRAINERS_EDITORS";
  }
  if (targetGroups.includes("TRAINERS")) return "TRAINERS";
  if (targetGroups.includes("EDITORS")) return "EDITORS";
  return "ALL_MEMBERS";
};

const isVideoUrl = (url) => /youtube|youtu\.be|vimeo/i.test(String(url || ""));

const formatDate = (value) => {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("de-DE", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (_error) {
    return "";
  }
};

const getTargetingLabel = (item) => {
  const map = {
    ALL_MEMBERS: "Alle Mitglieder",
    TRAINERS: "Trainer",
    EDITORS: "Editoren",
  };

  if (item?.scope === "SITE") {
    return item?.site?.siteName
      ? `Standort: ${item.site.siteName}`
      : "Standort-spezifisch";
  }

  const groups = Array.isArray(item?.targetGroups) ? item.targetGroups : [];
  const labels = groups.map((group) => map[group]).filter(Boolean);
  return labels.join(" + ") || "Alle Mitglieder";
};

const NEWS_FOLDERS = {
  INBOX: "INBOX",
  ARCHIVE: "ARCHIVE",
};

const isPlanLocked = (error) => error?.data?.value === "plan";

const normalizeFolderParam = (value) =>
  value === NEWS_FOLDERS.ARCHIVE ? NEWS_FOLDERS.ARCHIVE : NEWS_FOLDERS.INBOX;

const buildFolderSearch = (folder) =>
  normalizeFolderParam(folder) === NEWS_FOLDERS.ARCHIVE
    ? `?folder=${NEWS_FOLDERS.ARCHIVE}`
    : "";

const getPreviewData = (html, maxWords = 16, maxChars = 180) => {
  const plainText = getPlainTextFromHtml(html || "").trim();
  if (!plainText) {
    return { text: "", isTruncated: false };
  }
  const words = plainText.split(/\s+/).filter(Boolean);
  const isWordOverflow = words.length > maxWords;
  const isCharOverflow = plainText.length > maxChars;
  if (!isWordOverflow && !isCharOverflow) {
    return { text: plainText, isTruncated: false };
  }

  const byWords = words.slice(0, maxWords).join(" ").trim();
  const byChars = plainText.slice(0, maxChars).trim();

  const text = isWordOverflow && isCharOverflow
    ? (byWords.length <= byChars.length ? byWords : byChars)
    : isWordOverflow
      ? byWords
      : byChars;

  return {
    text,
    isTruncated: true,
  };
};

const getUserState = (item) => ({
  isRead: Boolean(item?.userState?.isRead),
  isArchivedForUser: Boolean(item?.userState?.isArchivedForUser),
});

const getStatusChip = (userState) => {
  const isRead = Boolean(userState?.isRead);
  const isArchivedForUser = Boolean(userState?.isArchivedForUser);
  if (isArchivedForUser) {
    return {
      label: "Im Archiv",
      color: "success",
      icon: "mdi:archive-outline",
    };
  }
  if (isRead) {
    return {
      label: "Gelesen",
      color: "default",
      icon: "mdi:check-circle-outline",
    };
  }
  return {
    label: "Neu",
    color: "secondary",
    icon: "mdi:bell-ring-outline",
  };
};

export default function NewsPage() {
  const navigate = useNavigate();
  const { isAdmin, isEditor, isUser, isMember, isSuperAdmin } = useAuth();

  const canManageNews =
    isSuperAdmin || isAdmin || isEditor || (isUser && !isMember);

  return (
    <>
      <Helmet>
        <title>Dashboard | Neuigkeiten</title>
      </Helmet>

      <Container maxWidth="lg" sx={{ pb: 4 }}>
        <Routes>
          <Route
            index
            element={
              <NewsListView
                canManageNews={canManageNews}
                onCreate={() => navigate("new")}
                onEdit={(id) => navigate(`${id}/edit`)}
              />
            }
          />
          <Route
            path="new"
            element={
              canManageNews ? (
                <NewsFormView mode="create" canManageNews={canManageNews} />
              ) : (
                <Navigate to="/dashboard/news" replace />
              )
            }
          />
          <Route
            path=":newsId/edit"
            element={
              canManageNews ? (
                <NewsFormView mode="edit" canManageNews={canManageNews} />
              ) : (
                <Navigate to="/dashboard/news" replace />
              )
            }
          />
          <Route path=":newsId" element={<NewsDetailView canManageNews={canManageNews} />} />
          <Route path="*" element={<Navigate to="/dashboard/news" replace />} />
        </Routes>
      </Container>
    </>
  );
}

function NewsListView({ canManageNews, onCreate, onEdit }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin, isSuperAdmin } = useAuth();

  const {
    data: news = [],
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetNewsQuery();

  // If the tenant doesn't have the NEWS plan, Admin+ users can still see system announcements
  const showSystemFallback = isError && isPlanLocked(error) && (isAdmin || isSuperAdmin);
  const {
    data: systemNews = [],
    isLoading: isSystemLoading,
    isFetching: isSystemFetching,
  } = useGetSystemNewsQuery(undefined, { skip: !showSystemFallback });

  const [deleteNews, deleteState] = useDeleteNewsMutation();
  const [updateNewsUserState, updateNewsUserStateState] = useUpdateNewsUserStateMutation();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activeFolder, setActiveFolder] = useState(() => normalizeFolderParam(searchParams.get("folder")));
  const [pendingStateId, setPendingStateId] = useState("");
  const [localUserStateByNewsId, setLocalUserStateByNewsId] = useState({});

  const displayedNews = showSystemFallback ? systemNews : news;

  const getMergedState = (item) => {
    if (!item?._id) return getUserState(item);
    const localState = localUserStateByNewsId[item._id];
    if (localState) return localState;
    return getUserState(item);
  };

  const inboxCount = displayedNews.filter((item) => !getMergedState(item).isArchivedForUser).length;
  const archiveCount = displayedNews.filter((item) => getMergedState(item).isArchivedForUser).length;
  const visibleNews = displayedNews.filter((item) => {
    const { isArchivedForUser } = getMergedState(item);
    return activeFolder === NEWS_FOLDERS.ARCHIVE ? isArchivedForUser : !isArchivedForUser;
  });

  const hasItems = Array.isArray(displayedNews) && displayedNews.length > 0;
  const hasVisibleNews = visibleNews.length > 0;

  useEffect(() => {
    const folderFromUrl = normalizeFolderParam(searchParams.get("folder"));
    setActiveFolder((prev) => (prev === folderFromUrl ? prev : folderFromUrl));
  }, [searchParams]);

  const changeFolder = (nextFolder) => {
    const normalizedFolder = normalizeFolderParam(nextFolder);
    setActiveFolder(normalizedFolder);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (normalizedFolder === NEWS_FOLDERS.ARCHIVE) {
        next.set("folder", NEWS_FOLDERS.ARCHIVE);
      } else {
        next.delete("folder");
      }
      return next;
    }, { replace: true });
  };

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    try {
      await deleteNews({
        id: deleteTarget._id,
        isSystemAnnouncement: Boolean(deleteTarget.isSystemAnnouncement),
      }).unwrap();
      setDeleteTarget(null);
    } catch (_error) {
      // handled by mutation state alert
    }
  };

  const patchNewsState = async (item, patch) => {
    if (!item?._id) return;
    const currentState = getMergedState(item);
    const optimisticState = { ...currentState };

    if (typeof patch?.isRead === "boolean") {
      optimisticState.isRead = patch.isRead;
    }
    if (typeof patch?.isArchivedForUser === "boolean") {
      optimisticState.isArchivedForUser = patch.isArchivedForUser;
      if (patch.isArchivedForUser) {
        optimisticState.isRead = true;
      }
    }

    setLocalUserStateByNewsId((prev) => ({ ...prev, [item._id]: optimisticState }));
    setPendingStateId(item._id);

    try {
      const response = await updateNewsUserState({ id: item._id, ...patch }).unwrap();
      const remoteState = getUserState(response?.item);
      setLocalUserStateByNewsId((prev) => ({ ...prev, [item._id]: remoteState }));
    } catch (_error) {
      setLocalUserStateByNewsId((prev) => ({ ...prev, [item._id]: currentState }));
    } finally {
      setPendingStateId("");
    }
  };

  return (
    <Stack spacing={3}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          background:
            "linear-gradient(140deg, rgba(25, 118, 210, 0.1), rgba(3, 169, 244, 0.07), rgba(76, 175, 80, 0.06))",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={1.5}
          >
            <Box>
              <Typography variant="h4">Neuigkeiten</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                {canManageNews
                  ? "Hier finden Sie aktuelle Informationen und können Neuigkeiten gezielt für Mitglieder und Teams veröffentlichen."
                  : "Hier finden Sie alle wichtigen Informationen in einer klaren Übersicht. Bereits gelesene Neuigkeiten können Sie bei Bedarf in Ihr persönliches Archiv verschieben."}
              </Typography>
            </Box>
            {canManageNews && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Iconify icon="mdi:plus" />}
                onClick={onCreate}
              >
                Neuigkeit erstellen
              </Button>
            )}
          </Stack>
          <Alert severity="info" icon={<Iconify icon="mdi:information-outline" />}>
            Wenn Sie eine Neuigkeit als gelesen markieren, können Sie sie in der Übersicht lassen oder in Ihr Archiv
            verschieben. Im Archiv bleibt sie weiterhin jederzeit verfügbar.
          </Alert>
          {hasItems && (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                p: 0.5,
                backgroundColor: "rgba(255,255,255,0.62)",
              }}
            >
              <Tabs
                value={activeFolder}
                onChange={(_event, value) => changeFolder(value)}
                variant="fullWidth"
                textColor="primary"
                indicatorColor="primary"
              >
                <Tab
                  value={NEWS_FOLDERS.INBOX}
                  label={`Aktuell (${inboxCount})`}
                  icon={<Iconify icon="mdi:newspaper-variant-outline" width={18} height={18} />}
                  iconPosition="start"
                />
                <Tab
                  value={NEWS_FOLDERS.ARCHIVE}
                  label={`Archiv (${archiveCount})`}
                  icon={<Iconify icon="mdi:archive-outline" width={18} height={18} />}
                  iconPosition="start"
                />
              </Tabs>
            </Paper>
          )}
        </Stack>
      </Paper>

      {(isLoading || isFetching || isSystemLoading || isSystemFetching) && <Loading />}

      {isError && !showSystemFallback && (
        <Alert severity="error" action={<Button onClick={refetch}>Erneut laden</Button>}>
          {error?.data?.message || "Neuigkeiten konnten nicht geladen werden."}
        </Alert>
      )}

      {showSystemFallback && (
        <Alert severity="info" icon={<Iconify icon="mdi:bullhorn-variant-outline" />}>
          Ihr Tarif enthält keine Neuigkeiten. Systemankündigungen von Martial Artministration werden dennoch angezeigt.
        </Alert>
      )}

      {deleteState.isError && (
        <Alert severity="error">
          {deleteState.error?.data?.message || "Löschen fehlgeschlagen."}
        </Alert>
      )}
      {updateNewsUserStateState.isError && (
        <Alert severity="error">
          {updateNewsUserStateState.error?.data?.message || "Status konnte nicht aktualisiert werden."}
        </Alert>
      )}

      {!isLoading && !isFetching && !isSystemLoading && !hasItems && (
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
          <Stack spacing={1.5} alignItems="center">
            <Iconify icon="mdi:newspaper-variant-outline" width={44} height={44} />
            <Typography variant="h6">Noch keine Neuigkeiten vorhanden</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
              Sobald Neuigkeiten veröffentlicht werden, erscheinen sie hier automatisch. So bleiben Mitglieder und
              Team stets auf dem aktuellen Stand.
            </Typography>
            {canManageNews && !showSystemFallback && (
              <Button variant="contained" color="secondary" onClick={onCreate}>
                Erste Neuigkeit erstellen
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {hasItems && hasVisibleNews && (
        <Grid container spacing={2.5}>
          {visibleNews.map((item) => (
            <Grid item xs={12} md={6} key={item._id}>
              <NewsCard
                item={item}
                userState={getMergedState(item)}
                canManageNews={canManageNews}
                onEdit={() => onEdit(item._id)}
                onDelete={() => setDeleteTarget(item)}
                onOpen={() => navigate(`${item._id}${buildFolderSearch(activeFolder)}`)}
                onMarkRead={() => patchNewsState(item, { isRead: true })}
                onArchive={() => patchNewsState(item, { isArchivedForUser: true })}
                onUnarchive={() => patchNewsState(item, { isArchivedForUser: false })}
                isPending={pendingStateId === item._id}
                allowStateActions={!showSystemFallback}
              />
            </Grid>
          ))}
        </Grid>
      )}
      {hasItems && !hasVisibleNews && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: "center" }}>
          <Stack spacing={1.25} alignItems="center">
            <Iconify
              icon={activeFolder === NEWS_FOLDERS.ARCHIVE ? "mdi:archive-outline" : "mdi:check-circle-outline"}
              width={40}
              height={40}
            />
            <Typography variant="h6">
              {activeFolder === NEWS_FOLDERS.ARCHIVE ? "Ihr Archiv ist aktuell leer" : "Alles aufgeräumt"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
              {activeFolder === NEWS_FOLDERS.ARCHIVE
                ? "Archivierte Neuigkeiten werden hier gesammelt. Verschieben Sie gelesene Inhalte bei Bedarf ins Archiv."
                : "In der aktuellen Übersicht sind keine Neuigkeiten mehr offen. Wechseln Sie zum Archiv, um ältere Inhalte zu sehen."}
            </Typography>
            {activeFolder === NEWS_FOLDERS.INBOX && (
              <Button variant="outlined" onClick={() => changeFolder(NEWS_FOLDERS.ARCHIVE)}>
                Archiv öffnen
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Neuigkeit löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Diese Neuigkeit wird archiviert und ist danach für Benutzer nicht mehr sichtbar.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleteState.isLoading}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function NewsCard({
  item,
  userState,
  canManageNews,
  onOpen,
  onEdit,
  onDelete,
  onMarkRead,
  onArchive,
  onUnarchive,
  isPending,
  allowStateActions,
}) {
  const previewData = useMemo(() => getPreviewData(item?.inhalt || "", 16), [item?.inhalt]);
  const subtitlePreview = useMemo(() => {
    const words = String(item?.untertitel || "").split(/\s+/).filter(Boolean);
    if (words.length <= 10) return String(item?.untertitel || "");
    return `${words.slice(0, 10).join(" ")} …`;
  }, [item?.untertitel]);
  const showUrl = Boolean(item?.url);
  const canEdit = Boolean(canManageNews && item?.canEdit);
  const canDelete = Boolean(canManageNews && item?.canDelete);
  const isSystemAnnouncement = Boolean(item?.isSystemAnnouncement);
  const isRead = Boolean(userState?.isRead);
  const isArchivedForUser = Boolean(userState?.isArchivedForUser);
  const statusChip = getStatusChip(userState);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "all 180ms ease",
        background: isArchivedForUser
          ? "linear-gradient(180deg, rgba(76, 175, 80, 0.08), rgba(76, 175, 80, 0.02))"
          : isRead
            ? "linear-gradient(180deg, rgba(120, 120, 120, 0.05), rgba(120, 120, 120, 0.01))"
            : "linear-gradient(180deg, rgba(33, 150, 243, 0.08), rgba(33, 150, 243, 0.02))",
        boxShadow: isSystemAnnouncement
          ? "0 8px 24px rgba(255, 152, 0, 0.18)"
          : isRead
            ? "0 8px 24px rgba(0,0,0,0.04)"
            : "0 10px 28px rgba(33, 150, 243, 0.12)",
        borderColor: isSystemAnnouncement
          ? "warning.light"
          : isArchivedForUser
            ? "success.light"
            : isRead
              ? "divider"
              : "primary.light",
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Stack spacing={1.75}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {isSystemAnnouncement ? (
                <Tooltip title="Systemankündigung von Martial Artministration – gilt für alle Mandanten">
                  <Chip
                    size="small"
                    color="warning"
                    icon={<Iconify icon="mdi:bullhorn-variant" width={14} height={14} />}
                    label="Systemankündigung"
                  />
                </Tooltip>
              ) : (
                <Chip size="small" color="primary" variant="outlined" label={getTargetingLabel(item)} />
              )}
              <Chip
                size="small"
                color={statusChip.color}
                icon={<Iconify icon={statusChip.icon} width={14} height={14} />}
                label={statusChip.label}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {formatDate(item?.createdAt)}
            </Typography>
          </Stack>

          <Box>
            <Typography variant="h6">{item?.titel}</Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
            >
              {subtitlePreview}
            </Typography>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: "background.paper",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
            >
              {previewData.text || "Keine Vorschau verfügbar."}
              {previewData.isTruncated && (
                <>
                  {"... "}
                  <Box
                    component="button"
                    type="button"
                    onClick={onOpen}
                    sx={{
                      border: 0,
                      p: 0,
                      m: 0,
                      background: "transparent",
                      color: "primary.main",
                      font: "inherit",
                      fontWeight: 600,
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    anzeigen
                  </Box>
                </>
              )}
            </Typography>
          </Paper>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1} useFlexGap>
            <Button
              size="medium"
              fullWidth
              variant="contained"
              color="secondary"
              onClick={onOpen}
              startIcon={<Iconify icon="mdi:arrow-right-circle-outline" />}
            >
              Anzeigen
            </Button>
            {showUrl && (
              <Button
                size="medium"
                fullWidth
                variant="contained"
                color="primary"
                component="a"
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<Iconify icon="mdi:open-in-new" width={16} height={16} />}
              >
                {isVideoUrl(item.url) ? "Zum Video" : "Mehr erfahren"}
              </Button>
            )}
          </Stack>

          {allowStateActions && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1} useFlexGap>
              {!isRead && (
                <Button
                  size="medium"
                  fullWidth
                  variant="contained"
                  color="success"
                  startIcon={<Iconify icon="mdi:check" />}
                  onClick={onMarkRead}
                  disabled={isPending}
                >
                  Als gelesen markieren
                </Button>
              )}
              {!isArchivedForUser && (
                <Button
                  size="medium"
                  fullWidth
                  variant={isRead ? "outlined" : "text"}
                  color="inherit"
                  startIcon={<Iconify icon="mdi:archive-arrow-down-outline" />}
                  onClick={onArchive}
                  disabled={isPending}
                >
                  {isRead ? "archivieren" : "Gelesen + archivieren"}
                </Button>
              )}
              {isArchivedForUser && (
                <Button
                  size="medium"
                  fullWidth
                  variant="outlined"
                  color="success"
                  startIcon={<Iconify icon="mdi:archive-arrow-up-outline" />}
                  onClick={onUnarchive}
                  disabled={isPending}
                >
                  Wieder in Aktuell anzeigen
                </Button>
              )}
            </Stack>
          )}

          {(canEdit || canDelete) && (
            <>
              <Divider />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap>
                {canEdit && (
                  <Button size="small" variant="outlined" onClick={onEdit}>
                    Bearbeiten
                  </Button>
                )}
                {canDelete && (
                  <Button size="small" color="error" variant="outlined" onClick={onDelete}>
                    Löschen
                  </Button>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function NewsDetailView({ canManageNews }) {
  const navigate = useNavigate();
  const { newsId } = useParams();
  const [searchParams] = useSearchParams();
  const { isAdmin, isSuperAdmin } = useAuth();
  const folder = normalizeFolderParam(searchParams.get("folder"));
  const listPath = `/dashboard/news${buildFolderSearch(folder)}`;

  const {
    data: apiNewsItem,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetNewsByIdQuery(newsId, { skip: !newsId });

  const showSystemFallback = isError && isPlanLocked(error) && (isAdmin || isSuperAdmin);
  const {
    data: systemNews = [],
    isLoading: isSystemLoading,
    isFetching: isSystemFetching,
    isError: isSystemError,
    error: systemError,
    refetch: refetchSystemNews,
  } = useGetSystemNewsQuery(undefined, { skip: !showSystemFallback });

  const selectedSystemNewsItem = useMemo(
    () => systemNews.find((item) => String(item?._id) === String(newsId)),
    [newsId, systemNews],
  );
  const item = showSystemFallback ? selectedSystemNewsItem : apiNewsItem;
  const safeHtml = useMemo(() => sanitizeRichTextHtml(item?.inhalt || ""), [item?.inhalt]);
  const allowStateActions = Boolean(item?._id) && !showSystemFallback;
  const canEdit = Boolean(canManageNews && item?.canEdit);
  const canDelete = Boolean(canManageNews && item?.canDelete);

  const [deleteNews, deleteState] = useDeleteNewsMutation();
  const [updateNewsUserState, updateNewsUserStateState] = useUpdateNewsUserStateMutation();
  const [pendingState, setPendingState] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [localUserState, setLocalUserState] = useState(null);

  useEffect(() => {
    if (!item?._id) return;
    setLocalUserState({
      isRead: Boolean(item?.userState?.isRead),
      isArchivedForUser: Boolean(item?.userState?.isArchivedForUser),
    });
  }, [item?._id, item?.userState?.isArchivedForUser, item?.userState?.isRead]);

  const mergedUserState = localUserState || getUserState(item);
  const mergedStatusChip = getStatusChip(mergedUserState);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(listPath, { replace: true });
  };

  const patchNewsState = async (patch) => {
    if (!item?._id || !allowStateActions) return;
    const currentState = mergedUserState;
    const optimisticState = { ...currentState };

    if (typeof patch?.isRead === "boolean") {
      optimisticState.isRead = patch.isRead;
    }
    if (typeof patch?.isArchivedForUser === "boolean") {
      optimisticState.isArchivedForUser = patch.isArchivedForUser;
      if (patch.isArchivedForUser) {
        optimisticState.isRead = true;
      }
    }

    setLocalUserState(optimisticState);
    setPendingState(true);

    try {
      const response = await updateNewsUserState({ id: item._id, ...patch }).unwrap();
      setLocalUserState(getUserState(response?.item));
    } catch (_error) {
      setLocalUserState(currentState);
    } finally {
      setPendingState(false);
    }
  };

  const handleDelete = async () => {
    if (!item?._id || !canDelete) return;
    try {
      await deleteNews({
        id: item._id,
        isSystemAnnouncement: Boolean(item.isSystemAnnouncement),
      }).unwrap();
      setDeleteDialogOpen(false);
      navigate(listPath, { replace: true });
    } catch (_error) {
      // handled via mutation state alert
    }
  };

  if (!newsId) {
    return <Alert severity="error">Ungültige Neuigkeit.</Alert>;
  }

  if (isLoading || isFetching || isSystemLoading || isSystemFetching) {
    return <Loading />;
  }

  if (isError && !showSystemFallback) {
    return (
      <Stack spacing={2}>
        <Alert severity="error" action={<Button onClick={refetch}>Erneut laden</Button>}>
          {error?.data?.message || "Neuigkeit konnte nicht geladen werden."}
        </Alert>
        <Button variant="outlined" onClick={handleBack} startIcon={<Iconify icon="mdi:arrow-left" />}>
          Zurück zur Übersicht
        </Button>
      </Stack>
    );
  }

  if (showSystemFallback && isSystemError) {
    return (
      <Stack spacing={2}>
        <Alert severity="error" action={<Button onClick={refetchSystemNews}>Erneut laden</Button>}>
          {systemError?.data?.message || "Systemankündigung konnte nicht geladen werden."}
        </Alert>
        <Button variant="outlined" onClick={handleBack} startIcon={<Iconify icon="mdi:arrow-left" />}>
          Zurück zur Übersicht
        </Button>
      </Stack>
    );
  }

  if (!item?._id) {
    return (
      <Stack spacing={2}>
        <Alert severity="warning">Die angeforderte Neuigkeit wurde nicht gefunden.</Alert>
        <Button variant="outlined" onClick={handleBack} startIcon={<Iconify icon="mdi:arrow-left" />}>
          Zurück zur Übersicht
        </Button>
      </Stack>
    );
  }

  const isRead = Boolean(mergedUserState?.isRead);
  const isArchivedForUser = Boolean(mergedUserState?.isArchivedForUser);

  return (
    <Stack spacing={2.5}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          background: item?.isSystemAnnouncement
            ? "linear-gradient(145deg, rgba(255, 152, 0, 0.14), rgba(255, 183, 77, 0.08), rgba(255,255,255,0.95))"
            : "linear-gradient(145deg, rgba(33, 150, 243, 0.14), rgba(3, 169, 244, 0.08), rgba(255,255,255,0.95))",
          borderColor: item?.isSystemAnnouncement ? "warning.light" : "primary.light",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={1.25}
          >
            <Button variant="text" onClick={handleBack} startIcon={<Iconify icon="mdi:arrow-left" />}>
              Zurück zur Übersicht
            </Button>
            <Typography variant="caption" color="text.secondary">
              {formatDate(item?.createdAt)}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {item?.isSystemAnnouncement ? (
              <Chip
                size="small"
                color="warning"
                icon={<Iconify icon="mdi:bullhorn-variant" width={14} height={14} />}
                label="Systemankündigung"
              />
            ) : (
              <Chip size="small" color="primary" variant="outlined" label={getTargetingLabel(item)} />
            )}
            <Chip
              size="small"
              color={mergedStatusChip.color}
              icon={<Iconify icon={mergedStatusChip.icon} width={14} height={14} />}
              label={mergedStatusChip.label}
            />
            <Chip
              size="small"
              color="default"
              variant="outlined"
              icon={<Iconify icon="mdi:account-circle-outline" width={14} height={14} />}
              label={item?.createdByName || "Unbekannt"}
            />
          </Stack>

          <Box>
            <Typography variant="h4" sx={{ mb: 0.5, wordBreak: "break-word", overflowWrap: "anywhere" }}>
              {item?.titel}
            </Typography>
            {item?.untertitel && (
              <Typography variant="h6" color="text.secondary" sx={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                {item.untertitel}
              </Typography>
            )}
          </Box>

          {item?.url && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1}>
              <Button
                variant="contained"
                color="primary"
                component="a"
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<Iconify icon="mdi:open-in-new" width={16} height={16} />}
              >
                {isVideoUrl(item.url) ? "Zum Video" : "Mehr erfahren"}
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>

      {showSystemFallback && (
        <Alert severity="info" icon={<Iconify icon="mdi:information-outline" />}>
          Ihr Tarif enthält keine Neuigkeiten. Diese Systemankündigung ist daher nur lesbar.
        </Alert>
      )}
      {deleteState.isError && (
        <Alert severity="error">
          {deleteState.error?.data?.message || "Löschen fehlgeschlagen."}
        </Alert>
      )}
      {updateNewsUserStateState.isError && (
        <Alert severity="error">
          {updateNewsUserStateState.error?.data?.message || "Status konnte nicht aktualisiert werden."}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Stack spacing={2}>
          <Box
            sx={{
              "& p": { mt: 0, mb: 1.2 },
              "& ul, & ol": { pl: 3, mb: 1.2 },
              "& h1, & h2, & h3": { mt: 1.5, mb: 1 },
              "& a": { color: "primary.main" },
              wordBreak: "break-word",
              overflowWrap: "anywhere",
            }}
            dangerouslySetInnerHTML={{ __html: safeHtml || "<p>Kein Inhalt vorhanden.</p>" }}
          />

          {allowStateActions && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1} useFlexGap>
              {!isRead && (
                <Button
                  size="medium"
                  fullWidth
                  variant="contained"
                  color="success"
                  startIcon={<Iconify icon="mdi:check" />}
                  onClick={() => patchNewsState({ isRead: true })}
                  disabled={pendingState}
                >
                  Als gelesen markieren
                </Button>
              )}
              {!isArchivedForUser && (
                <Button
                  size="medium"
                  fullWidth
                  variant={isRead ? "outlined" : "text"}
                  color="inherit"
                  startIcon={<Iconify icon="mdi:archive-arrow-down-outline" />}
                  onClick={() => patchNewsState({ isArchivedForUser: true })}
                  disabled={pendingState}
                >
                  {isRead ? "archivieren" : "Gelesen + archivieren"}
                </Button>
              )}
              {isArchivedForUser && (
                <Button
                  size="medium"
                  fullWidth
                  variant="outlined"
                  color="success"
                  startIcon={<Iconify icon="mdi:archive-arrow-up-outline" />}
                  onClick={() => patchNewsState({ isArchivedForUser: false })}
                  disabled={pendingState}
                >
                  Wieder in Aktuell anzeigen
                </Button>
              )}
            </Stack>
          )}

          {(canEdit || canDelete) && (
            <>
              <Divider />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap>
                {canEdit && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(`/dashboard/news/${item._id}/edit`)}
                  >
                    Bearbeiten
                  </Button>
                )}
                {canDelete && (
                  <Button size="small" color="error" variant="outlined" onClick={() => setDeleteDialogOpen(true)}>
                    Löschen
                  </Button>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </Paper>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Neuigkeit löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Diese Neuigkeit wird archiviert und ist danach für Benutzer nicht mehr sichtbar.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleteState.isLoading}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function NewsFormView({ mode, canManageNews }) {
  const navigate = useNavigate();
  const { newsId } = useParams();
  const { isSuperAdmin } = useAuth();
  const isEdit = mode === "edit";

  const { data: sites = [] } = useGetSitesQuery(undefined, { skip: !canManageNews });
  const { data: existingNews, isLoading: isLoadingNews, isError: isNewsError } = useGetNewsByIdQuery(newsId, {
    skip: !isEdit || !newsId,
  });

  const [createNews, createState] = useCreateNewsMutation();
  const [updateNews, updateState] = useUpdateNewsMutation();

  const [titel, setTitel] = useState("");
  const [untertitel, setUntertitel] = useState("");
  const [inhalt, setInhalt] = useState("");
  const [url, setUrl] = useState("");
  const [targeting, setTargeting] = useState("ALL_MEMBERS");
  const [siteId, setSiteId] = useState("");
  const [isSystemAnnouncement, setIsSystemAnnouncement] = useState(false);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (!isEdit || !existingNews) return;
    setTitel(existingNews?.titel || "");
    setUntertitel(existingNews?.untertitel || "");
    setInhalt(existingNews?.inhalt || "");
    setUrl(existingNews?.url || "");
    setIsSystemAnnouncement(Boolean(existingNews?.isSystemAnnouncement));
    setTargeting(mapPayloadToTargeting(existingNews));
    setSiteId(existingNews?.site?._id || existingNews?.site || "");
  }, [existingNews, isEdit]);

  const isSubmitting = createState.isLoading || updateState.isLoading;
  const targetingMeta = TARGETING_OPTIONS.find((opt) => opt.value === targeting);
  const requiresSite = targeting === "SITE_MEMBERS" && !isSystemAnnouncement;

  useEffect(() => {
    if (!requiresSite) return;
    if (siteId) return;
    if (Array.isArray(sites) && sites.length === 1) {
      setSiteId(sites[0]._id);
    }
  }, [requiresSite, siteId, sites]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setValidationError("");

    const safeHtml = sanitizeRichTextHtml(inhalt);
    const plainText = getPlainTextFromHtml(safeHtml);

    if (!titel.trim() || !untertitel.trim() || !plainText) {
      setValidationError("Bitte Titel, Untertitel und Inhalt ausfüllen.");
      return;
    }

    if (requiresSite && !siteId) {
      setValidationError("Bitte wählen Sie einen Standort für die standortspezifische Neuigkeit.");
      return;
    }

    const mappedTargeting = isSystemAnnouncement
      ? { scope: "GLOBAL", targetGroups: ["ALL_MEMBERS"] }
      : mapTargetingToPayload(targeting);

    const payload = {
      titel: titel.trim(),
      untertitel: untertitel.trim(),
      inhalt: safeHtml,
      url: url.trim(),
      scope: mappedTargeting.scope,
      targetGroups: mappedTargeting.targetGroups,
      site: requiresSite ? siteId : null,
      isSystemAnnouncement,
    };

    try {
      if (isEdit) {
        await updateNews({ id: newsId, ...payload }).unwrap();
      } else {
        await createNews(payload).unwrap();
      }
      navigate("/dashboard/news");
    } catch (_error) {
      // handled in alert below
    }
  };

  if (isEdit && isLoadingNews) {
    return <Loading />;
  }

  if (isEdit && isNewsError) {
    return <Alert severity="error">Neuigkeit konnte nicht geladen werden.</Alert>;
  }

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
        >
          <Box>
            <Typography variant="h5">
              {isEdit ? "Neuigkeit bearbeiten" : "Neue Neuigkeit erstellen"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Füllen Sie Titel, Untertitel und Inhalt aus. Wählen Sie danach explizit, für wen diese Neuigkeit sichtbar
              sein soll.
            </Typography>
          </Box>
          <Button variant="outlined" onClick={() => navigate("/dashboard/news")}>Zurück</Button>
        </Stack>

        {isSuperAdmin && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              borderColor: isSystemAnnouncement ? "warning.light" : "divider",
              background: isSystemAnnouncement
                ? "rgba(255, 152, 0, 0.06)"
                : undefined,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  Systemankündigung
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Systemankündigungen sind für alle Mandanten-Admins sichtbar – unabhängig von deren Tarif. Nur
                  SuperAdmins können Systemankündigungen erstellen und bearbeiten.
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={isSystemAnnouncement}
                    onChange={(e) => setIsSystemAnnouncement(e.target.checked)}
                    color="warning"
                    disabled={isEdit && Boolean(existingNews?.isSystemAnnouncement) !== isSystemAnnouncement && !isEdit}
                  />
                }
                label=""
                sx={{ m: 0 }}
              />
            </Stack>
          </Paper>
        )}

        {validationError && <Alert severity="warning">{validationError}</Alert>}

        {(createState.isError || updateState.isError) && (
          <Alert severity="error">
            {createState.error?.data?.message ||
              updateState.error?.data?.message ||
              "Speichern fehlgeschlagen."}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Titel"
                value={titel}
                onChange={(event) => setTitel(event.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Untertitel"
                value={untertitel}
                onChange={(event) => setUntertitel(event.target.value)}
                fullWidth
                required
              />
            </Grid>
            {!isSystemAnnouncement && (
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Empfängergruppe"
                  value={targeting}
                  onChange={(event) => setTargeting(event.target.value)}
                  fullWidth
                >
                  {TARGETING_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                  {targetingMeta?.helper}
                </Typography>
              </Grid>
            )}
            {isSystemAnnouncement && (
              <Grid item xs={12} md={6}>
                <Alert severity="warning" icon={<Iconify icon="mdi:bullhorn-variant" />}>
                  Diese Neuigkeit wird als Systemankündigung für alle Mandanten-Admins veröffentlicht.
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                label="URL (optional)"
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                placeholder="https://..."
                fullWidth
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                Optionaler Link, z. B. zu Video, Website oder weiterführenden Informationen.
              </Typography>
            </Grid>

            {requiresSite && (
              <Grid item xs={12}>
                <TextField
                  select
                  label="Standort"
                  value={siteId}
                  onChange={(event) => setSiteId(event.target.value)}
                  fullWidth
                  required
                  disabled={sites.length === 1}
                >
                  {sites.map((site) => (
                    <MenuItem key={site._id} value={site._id}>
                      {site.siteName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Inhalt
              </Typography>
              <SimpleRichTextEditor value={inhalt} onChange={setInhalt} disabled={isSubmitting} minHeight={260} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                Unterstützt Überschriften, Listen, fett/kursiv und Links.
              </Typography>
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={() => navigate("/dashboard/news")}>
              Abbrechen
            </Button>
            <Button type="submit" variant="contained" color="secondary" disabled={isSubmitting}>
              {isEdit ? "Änderungen speichern" : "Neuigkeit speichern"}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}
